'use client'

import { useQuery } from '@tanstack/react-query'
import { EvmToken } from 'sushi/evm'
import type { EvmAddress } from 'sushi/evm'

const KATANA_DISCOVERY_URL = 'https://api.katana.network/v1/tokens/discovery'
const KATANA_CHAIN_ID = 747474

interface DiscoveryToken {
  address: string
  name: string
  symbol: string
  current_price: number
  price_change_percentage_24h: number
  market_cap: number
  fully_diluted_valuation: number
  sparkline_in_7d: { price: number[] }
}

interface DiscoveryResponse {
  success: boolean
  data: DiscoveryToken[]
}

export interface Token {
  token: EvmToken
  price: number
  priceChangePercentage1d: number
  marketCapUSD: number
  sparkline7d: number[]
}

export interface TokensResponse {
  count: number
  data: Token[]
}

async function getTokens(): Promise<TokensResponse> {
  try {
    const response = await fetch(KATANA_DISCOVERY_URL)
    if (!response.ok) throw new Error(`Discovery API error: ${response.status}`)

    const result: DiscoveryResponse = await response.json()

    if (result.success && result.data) {
      return {
        count: result.data.length,
        data: result.data.map((t) => ({
          token: new EvmToken({
            address: t.address as EvmAddress,
            chainId: KATANA_CHAIN_ID,
            symbol: t.symbol,
            name: t.name,
            decimals: 18,
          }),
          price: t.current_price,
          priceChangePercentage1d: t.price_change_percentage_24h,
          marketCapUSD: t.market_cap,
          sparkline7d: t.sparkline_in_7d?.price ?? [],
        })),
      }
    }
  } catch (error) {
    console.error('getTokens error', error)
  }

  return { count: 0, data: [] }
}

export function useTokens(
  _args: { chainId: string | number },
  shouldFetch = true,
) {
  return useQuery<TokensResponse>({
    queryKey: ['explore-tokens'],
    queryFn: getTokens,
    enabled: Boolean(shouldFetch),
    staleTime: 5 * 60 * 1000,
  })
}
